/**
 * LearnerIdPrompt — dismissible floating card that lets any signed-in
 * user (student or community) link the learner ID their instructor gave
 * them. Linking stores mc_learner_id / mc_learner_name so every mystery
 * case attempt lands under their name in class analytics.
 *
 * Shows only when: signed in + no ID linked + not previously dismissed.
 * The X close remembers dismissal (localStorage) so it never nags.
 */
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { BadgeCheck, X, CheckCircle2 } from "lucide-react";
import { getIdentity } from "../lib/learnerIdentity";

const API = process.env.REACT_APP_BACKEND_URL;
const CARD = "#fffdfa";
const BG = "#faf6ee";
const EDGE = "#e3dccb";
const COCOA = "#6B8F71";
const ACCENT = "#6B8F71";
const INK = "#2b261c";
const SUB = "#5d5748";
const GREEN = "#4C7A54";
const RED = "#b3403c";
const DISPLAY = "'Plus Jakarta Sans', system-ui, sans-serif";
const BODY = "'IBM Plex Sans', system-ui, sans-serif";

const DISMISS_KEY = "mc_lid_prompt_dismissed";

export default function LearnerIdPrompt() {
  const location = useLocation();
  const [identity, setIdentityState] = useState(() => getIdentity());
  const [linkedId, setLinkedId] = useState(() => localStorage.getItem("mc_learner_id") || "");
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(DISMISS_KEY) === "1");
  const [input, setInput] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [justLinked, setJustLinked] = useState(null);

  useEffect(() => {
    const sync = () => {
      setIdentityState(getIdentity());
      setLinkedId(localStorage.getItem("mc_learner_id") || "");
      setDismissed(localStorage.getItem(DISMISS_KEY) === "1");
    };
    window.addEventListener("storage", sync);
    window.addEventListener("maldek-identity-change", sync);
    return () => {
      window.removeEventListener("storage", sync);
      window.removeEventListener("maldek-identity-change", sync);
    };
  }, []);

  // The mystery hub already has its own inline claim panel.
  if (location.pathname.startsWith("/mystery")) return null;
  if (!identity || dismissed || (linkedId && !justLinked)) return null;

  const close = () => {
    localStorage.setItem(DISMISS_KEY, "1");
    setDismissed(true);
  };

  const link = async () => {
    const code = input.trim().toUpperCase();
    if (!code) return;
    setBusy(true); setErr("");
    try {
      const r = await fetch(`${API}/api/mystery/learner-id/${encodeURIComponent(code)}`);
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Learner ID not found");
      localStorage.setItem("mc_learner_id", d.learner_id);
      localStorage.setItem("mc_learner_name", d.name || d.learner_id);
      setLinkedId(d.learner_id);
      setJustLinked(d);
      setTimeout(() => { setJustLinked(null); }, 3500);
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 16 }}
        className="fixed bottom-4 left-4 z-40 w-[320px] max-w-[calc(100vw-2rem)] rounded-2xl p-4 no-print"
        style={{ background: CARD, border: `1px solid ${EDGE}`, boxShadow: "0 16px 40px rgba(38,41,46,0.16)", fontFamily: BODY }}
        data-testid="learner-id-prompt"
      >
        {justLinked ? (
          <div className="flex items-start gap-2.5" data-testid="learner-id-prompt-success">
            <CheckCircle2 className="h-4.5 w-4.5 mt-0.5 shrink-0" style={{ color: GREEN }} />
            <div>
              <p className="text-[13.5px] font-extrabold" style={{ color: INK, fontFamily: DISPLAY }}>
                Linked as {justLinked.name}
              </p>
              <p className="mt-0.5 text-[12px]" style={{ color: SUB }}>
                <span className="font-mono font-bold" style={{ color: COCOA }}>{justLinked.learner_id}</span> — your case attempts now count under your name.
              </p>
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2">
                <BadgeCheck className="h-4 w-4 shrink-0" style={{ color: COCOA }} />
                <p className="text-[13.5px] font-extrabold" style={{ color: INK, fontFamily: DISPLAY }}>Have a learner ID?</p>
              </div>
              <button onClick={close} aria-label="Close" data-testid="learner-id-prompt-close"
                className="rounded-md p-1 transition-colors hover:bg-black/5" style={{ color: SUB }}>
                <X className="h-4 w-4" />
              </button>
            </div>
            <p className="mt-1.5 text-[12px] leading-relaxed" style={{ color: SUB }}>
              If your instructor gave you an ID, link it so your case attempts appear under your name in class analytics.
            </p>
            <div className="mt-3 flex items-center gap-2">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value.toUpperCase())}
                onKeyDown={(e) => e.key === "Enter" && link()}
                placeholder="e.g. AMA-4821"
                data-testid="learner-id-prompt-input"
                className="flex-1 min-w-0 rounded-full px-3.5 py-2 text-[12.5px] font-semibold outline-none"
                style={{ background: BG, border: `1px solid ${EDGE}`, color: INK, fontFamily: BODY }}
              />
              <button onClick={link} disabled={busy || !input.trim()} data-testid="learner-id-prompt-link-btn"
                className="rounded-full px-4 py-2 text-[12px] font-bold disabled:opacity-40 shrink-0"
                style={{ background: COCOA, color: "#fffdfa", fontFamily: BODY }}>
                {busy ? "…" : "Link"}
              </button>
            </div>
            {err && <p className="mt-2 text-[11.5px] font-semibold" style={{ color: RED }} data-testid="learner-id-prompt-error">{err}</p>}
            <button onClick={close} data-testid="learner-id-prompt-no-id"
              className="mt-2 text-[11.5px] font-bold underline underline-offset-2 hover:opacity-75" style={{ color: SUB }}>
              I don&rsquo;t have one — don&rsquo;t ask again
            </button>
          </>
        )}
      </motion.div>
    </AnimatePresence>
  );
}
